import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/ascend/AppShell";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { isoDate, weekStartOf, formatWeekRange, addDays, parseIsoDate } from "@/lib/time";
import { ArrowRight, ArrowLeft, ChevronLeft, ChevronRight, Save, Sparkles, Heart } from "lucide-react";

const MOOD_EMOJI = { 1: "😔", 2: "😐", 3: "🙂", 4: "😊", 5: "🌟" };

export default function Reflection() {
  const [weekStart, setWeekStart] = useState(() => weekStartOf(new Date()));
  const [subjects, setSubjects] = useState([]);
  const [state, setState] = useState({
    what_worked: "", what_drained: "", one_change: "", next_week_focus_subject_id: null,
  });
  const [autoStats, setAutoStats] = useState(null);
  const [existing, setExisting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const ws = useMemo(() => isoDate(weekStart), [weekStart]);

  useEffect(() => {
    (async () => {
      try {
        const s = await api.get("/subjects");
        setSubjects(s.data.items || []);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, []);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/weekly-reflections/${ws}`);
        setAutoStats(data.auto_stats);
        setExisting(data.reflection);
        if (data.reflection) {
          setState({
            what_worked: data.reflection.what_worked || "",
            what_drained: data.reflection.what_drained || "",
            one_change: data.reflection.one_change || "",
            next_week_focus_subject_id: data.reflection.next_week_focus_subject_id || null,
          });
        } else {
          setState({ what_worked: "", what_drained: "", one_change: "", next_week_focus_subject_id: null });
        }
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [ws]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/weekly-reflections", {
        week_start_date: ws,
        what_worked: state.what_worked,
        what_drained: state.what_drained,
        one_change: state.one_change,
        next_week_focus_subject_id: state.next_week_focus_subject_id,
      });
      setExisting(data.reflection);
      setAutoStats(data.auto_stats);
      toast.success("Reflection saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const subjectMap = useMemo(() => {
    const m = new Map();
    subjects.forEach((s) => m.set(s.id, s));
    return m;
  }, [subjects]);

  return (
    <AppShell>
      <Toaster richColors position="top-center" />

      <div className="pt-2 flex flex-wrap items-end justify-between gap-3 mb-6 fade-up">
        <div>
          <span className="eyebrow">looking back, gently</span>
          <h1 className="font-display text-4xl sm:text-5xl mt-1" style={{ color: "var(--text)" }}>Weekly reflection</h1>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-ghost !px-3 !py-2" onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="reflection-prev-week"><ChevronLeft size={16} /></button>
          <div className="px-4 py-2 rounded-full text-sm font-medium card-soft-2" data-testid="reflection-week-range">
            {formatWeekRange(weekStart)}
          </div>
          <button className="btn-ghost !px-3 !py-2" onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="reflection-next-week"><ChevronRight size={16} /></button>
        </div>
      </div>

      {loading ? (
        <div className="text-muted">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: auto stats */}
          <aside className="lg:col-span-5" data-testid="reflection-auto-stats">
            <div className="card-soft p-6 fade-up">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={14} strokeWidth={1.5} style={{ color: "var(--primary)" }} />
                <span className="eyebrow">the numbers, softly</span>
              </div>
              <h3 className="font-display text-2xl leading-tight mb-4" style={{ color: "var(--text)" }}>this week</h3>

              <div className="grid grid-cols-3 gap-3 mb-5">
                <StatChip label="minutes" value={autoStats?.total_minutes ?? 0} testid="ref-total-min" />
                <StatChip label="blocks" value={autoStats?.blocks_completed ?? 0} testid="ref-blocks" />
                <StatChip label="streak" value={`${autoStats?.streak_days ?? 0}d`} testid="ref-streak" />
              </div>

              {autoStats?.grace_day_in_week && (
                <div
                  className="mb-5 rounded-2xl px-4 py-3 text-sm leading-relaxed"
                  style={{
                    background: "color-mix(in oklab, var(--primary-soft) 45%, transparent)",
                    color: "var(--text)",
                    border: "1px solid var(--border)",
                  }}
                  data-testid="ref-grace-note"
                >
                  ⛅ This week included a grace day — everyone needs one.
                </div>
              )}

              {(autoStats?.warm_moments || []).length > 0 && (
                <WarmMomentsCard moments={autoStats.warm_moments} />
              )}

              <div className="mb-5">
                <div className="text-xs uppercase tracking-wider text-muted mb-2">avg mood</div>
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-3xl" style={{ color: "var(--text)" }}>{autoStats?.avg_mood ?? "—"}</span>
                  {autoStats?.top_mood_day && (
                    <span className="text-sm text-muted">top day {autoStats.top_mood_day}</span>
                  )}
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wider text-muted mb-2">hours by subject</div>
                <div className="space-y-2">
                  {(autoStats?.subjects || []).length === 0 && (
                    <div className="font-hand text-base text-muted">nothing logged this week (yet)</div>
                  )}
                  {(autoStats?.subjects || []).map((s) => {
                    const targetMin = (s.target_hours || 0) * 60;
                    const pct = targetMin > 0 ? Math.min(100, Math.round((s.minutes / targetMin) * 100)) : 0;
                    const hours = (s.minutes / 60).toFixed(2).replace(/\.?0+$/, "");
                    return (
                      <div key={s.subject_id}>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                            <span style={{ color: "var(--text)" }}>{s.name}</span>
                          </span>
                          <span className="text-muted">{hours}h{s.target_hours ? ` / ${s.target_hours}h` : ""}</span>
                        </div>
                        <div className="progress-track"><div className="progress-fill" style={{ width: `${pct}%`, background: s.color }} /></div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </aside>

          {/* Right: prompts */}
          <section className="lg:col-span-7 space-y-4" data-testid="reflection-form">
            <PromptCard
              label="what worked this week?"
              placeholder="tiny things count — a morning walk, one full focus block, a decent night's sleep…"
              value={state.what_worked}
              onChange={(v) => setState({ ...state, what_worked: v })}
              testid="ref-what-worked"
            />
            <PromptCard
              label="what drained you?"
              placeholder="be honest with yourself. nothing bad about noticing."
              value={state.what_drained}
              onChange={(v) => setState({ ...state, what_drained: v })}
              testid="ref-what-drained"
            />
            <PromptCard
              label="one small thing to change next week?"
              placeholder="one thing. small enough to keep."
              value={state.one_change}
              onChange={(v) => setState({ ...state, one_change: v })}
              testid="ref-one-change"
            />

            <div className="card-soft p-5" data-testid="ref-focus-picker">
              <span className="eyebrow">next week's focus (optional)</span>
              <div className="font-display text-lg leading-snug mb-3" style={{ color: "var(--text)" }}>choose one subject to lean into</div>
              <div className="flex flex-wrap gap-2">
                {subjects.map((s) => (
                  <button
                    type="button"
                    key={s.id}
                    onClick={() => setState({ ...state, next_week_focus_subject_id: state.next_week_focus_subject_id === s.id ? null : s.id })}
                    className="flex items-center gap-2 px-3 py-2 rounded-full text-sm"
                    style={{
                      background: state.next_week_focus_subject_id === s.id ? `color-mix(in oklab, ${s.color} 22%, var(--surface))` : "var(--surface-2)",
                      border: `1px solid ${state.next_week_focus_subject_id === s.id ? s.color : "var(--border)"}`,
                      color: "var(--text)",
                    }}
                    data-testid={`ref-focus-${s.name.toLowerCase()}`}
                  >
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
                    {s.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={save}
                disabled={busy}
                className="btn-primary flex items-center gap-2 disabled:opacity-60"
                data-testid="reflection-save-btn"
              >
                <Save size={14} strokeWidth={1.5} /> {existing ? "Save changes" : "Save reflection"}
              </button>
            </div>
          </section>
        </div>
      )}
    </AppShell>
  );
}

function StatChip({ label, value, testid }) {
  return (
    <div className="rounded-2xl px-3 py-3 text-center" style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }} data-testid={testid}>
      <div className="font-display text-2xl leading-none" style={{ color: "var(--text)" }}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted mt-1">{label}</div>
    </div>
  );
}

function PromptCard({ label, placeholder, value, onChange, testid }) {
  return (
    <div className="card-soft p-5 fade-up">
      <span className="eyebrow">{label}</span>
      <textarea
        className="input-soft mt-2"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        style={{ minHeight: 100 }}
        data-testid={testid}
      />
    </div>
  );
}

function WarmMomentsCard({ moments }) {
  const tilts = [-1.5, 0.8, -0.6];
  return (
    <div className="mb-5" data-testid="ref-warm-moments">
      <div className="flex items-center gap-2 mb-2">
        <Heart size={12} strokeWidth={1.5} style={{ color: "var(--accent)" }} />
        <span className="eyebrow text-[10px]">warm moments this week</span>
        <a
          href="/gratitude"
          className="ml-auto text-[11px] italic"
          style={{ color: "var(--primary)" }}
          data-testid="ref-warm-see-wall"
        >
          see the whole wall →
        </a>
      </div>
      <div className="space-y-2">
        {moments.map((w, i) => (
          <div
            key={w.date + "-" + i}
            className="rounded-2xl px-4 py-3"
            style={{
              background: "color-mix(in oklab, var(--primary-soft) 34%, var(--surface))",
              border: "1px solid var(--border)",
              transform: `rotate(${tilts[i % tilts.length]}deg)`,
              boxShadow: "0 3px 8px rgba(0,0,0,0.04)",
            }}
            data-testid={`ref-warm-moment-${i}`}
          >
            <div className="italic leading-snug" style={{ color: "var(--text)", fontFamily: "var(--font-hand, inherit)", fontSize: "1.05rem" }}>
              {w.gratitude_text}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-muted mt-1.5" style={{ fontFamily: "serif" }}>
              {w.day_of_week}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

